import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const activeSlug = slug || searchParams.get('tab') || 'all';

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  const filteredArticles = activeSlug === 'all'
    ? articles
    : articles.filter((a) => a.category === activeSlug);

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://cnmfnm.cn' },
          { '@type': 'ListItem', position: 2, name: '文章分类', item: 'https://cnmfnm.cn/category' },
          { '@type': 'ListItem', position: 3, name: activeCategory.name },
        ],
      },
      {
        '@type': 'WebPage',
        url: `https://cnmfnm.cn/category/${activeSlug}`,
        name: `${activeCategory.name} - 皇冠App`,
        isPartOf: { '@id': 'https://cnmfnm.cn/#website' },
        inLanguage: 'zh-CN',
      },
    ],
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(categoryJsonLd);
    script.id = 'schema-category';
    const existing = document.getElementById('schema-category');
    if (existing) existing.remove();
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-category');
      if (el) el.remove();
    };
  }, [activeSlug]);

  return (
    <PageLayout>
      <SEO
        title={`${activeCategory.name} - 皇冠App文章分类`}
        description={`浏览皇冠App ${activeCategory.name}相关文章，了解皇冠App的最新动态、使用教程和安全指南。`}
        keywords={`皇冠App,${activeCategory.name},皇冠App教程,皇冠App文章`}
        canonicalUrl={`https://cnmfnm.cn/category/${activeSlug}`}
      />

      <div className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '文章分类', path: '/category' },
                { label: activeCategory.name },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto py-10 md:py-14">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-60 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">文章分类</h3>
                <nav className="flex lg:flex-col gap-1 flex-wrap">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-sm whitespace-nowrap transition-colors duration-200 ${
                        activeSlug === cat.slug
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${cat.icon} text-sm w-3.5 h-3.5 flex items-center justify-center`}></i>
                      </div>
                      {cat.name}
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>

            <main className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                {activeCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 mb-8">
                共 {filteredArticles.length} 篇文章
              </p>

              {filteredArticles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-inbox-line text-4xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-200"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs text-primary-600 font-medium bg-primary-50 px-2.5 py-1 rounded-full">
                            {categories.find((c) => c.slug === article.category)?.name || article.category}
                          </span>
                          <span className="text-xs text-foreground-400">{article.publishDate}</span>
                          <span className="text-xs text-foreground-400">{article.readTime}阅读</span>
                        </div>
                        <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                          {article.title}
                        </h2>
                        <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}